/**
 * Typed sessionStorage wrapper for auth state.
 *
 * sessionStorage (not localStorage): tokens evaporate when the tab closes,
 * which is the safer SPA default. A determined XSS attacker can still
 * exfiltrate from sessionStorage — for stronger isolation, move to
 * HttpOnly cookies via a backend exchange endpoint (later).
 */

const KEY_PREFIX = 'projectbuddy.auth.'
const KEYS = {
  idToken: `${KEY_PREFIX}id_token`,
  refreshToken: `${KEY_PREFIX}refresh_token`,
  expiresAt: `${KEY_PREFIX}expires_at`,
  pkceVerifier: `${KEY_PREFIX}pkce_verifier`,
  oauthState: `${KEY_PREFIX}oauth_state`,
  postLoginRedirect: `${KEY_PREFIX}post_login_redirect`,
} as const

export interface StoredTokens {
  idToken: string
  refreshToken: string | null
  expiresAt: number  // unix seconds
}

export function readTokens(): StoredTokens | null {
  const idToken = sessionStorage.getItem(KEYS.idToken)
  const expiresAtRaw = sessionStorage.getItem(KEYS.expiresAt)
  if (!idToken || !expiresAtRaw) return null
  const expiresAt = parseInt(expiresAtRaw, 10)
  if (Number.isNaN(expiresAt) || expiresAt <= nowSeconds()) {
    clearTokens()
    return null
  }
  return {
    idToken,
    refreshToken: sessionStorage.getItem(KEYS.refreshToken),
    expiresAt,
  }
}

export function writeTokens(t: StoredTokens): void {
  sessionStorage.setItem(KEYS.idToken, t.idToken)
  sessionStorage.setItem(KEYS.expiresAt, String(t.expiresAt))
  if (t.refreshToken) sessionStorage.setItem(KEYS.refreshToken, t.refreshToken)
}

export function clearTokens(): void {
  sessionStorage.removeItem(KEYS.idToken)
  sessionStorage.removeItem(KEYS.refreshToken)
  sessionStorage.removeItem(KEYS.expiresAt)
}

// PKCE / state helpers — these are short-lived (login → callback only).

export function stashPkce(verifier: string, state: string, postLoginPath: string): void {
  sessionStorage.setItem(KEYS.pkceVerifier, verifier)
  sessionStorage.setItem(KEYS.oauthState, state)
  sessionStorage.setItem(KEYS.postLoginRedirect, postLoginPath)
}

export function readAndClearPkce(): {
  verifier: string | null
  state: string | null
  postLoginPath: string
} {
  const verifier = sessionStorage.getItem(KEYS.pkceVerifier)
  const state = sessionStorage.getItem(KEYS.oauthState)
  const postLoginPath = sessionStorage.getItem(KEYS.postLoginRedirect) || '/'
  sessionStorage.removeItem(KEYS.pkceVerifier)
  sessionStorage.removeItem(KEYS.oauthState)
  sessionStorage.removeItem(KEYS.postLoginRedirect)
  return { verifier, state, postLoginPath }
}

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000)
}
